import React, { useState, useEffect } from "react";
import axios from "../../utils/axiosInstance";
import Button from "../../components/ui/Button";
import TaskSetForm from "../../components/TaskManagement/forms/TaskSetForm";

const TaskSetEditor = ({ isOpen, onClose, taskSet, onSaved }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    tags: [],
    estimatedHours: "",
    tasks: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && taskSet) {
      // Convert taskRefs to tasks format
      const tasks = (taskSet.taskRefs || []).map((ref, index) => {
        const task = typeof ref.taskId === 'object' ? ref.taskId : { _id: ref.taskId };
        
        // Convert nested subtaskRefs to subTasks
        const subTasks = (task.subtaskRefs || []).map((subRef, subIndex) => {
          const subtask = typeof subRef.subtaskId === 'object' ? subRef.subtaskId : { _id: subRef.subtaskId };
          return {
            id: subtask._id,
            _id: subtask._id,
            name: subtask.name || "",
            description: subtask.description || "",
            weight: subRef.weight || subtask.defaultWeight || subtask.weight || 1,
            estimatedHours: subtask.defaultEstimatedHours || subtask.estimatedHours || "",
            registryId: subtask._id,
            sortOrder: subIndex
          };
        });

        return {
          id: task._id,
          _id: task._id,
          name: task.name || "",
          description: task.description || "",
          weight: ref.weight || task.defaultWeight || task.weight || 1,
          estimatedHours: task.defaultEstimatedHours || task.estimatedHours || "",
          tags: task.tags || [],
          registryId: task._id,
          sortOrder: index,
          subTasks
        };
      });

      setFormData({
        name: taskSet.name || "",
        description: taskSet.description || "",
        tags: taskSet.tags || [],
        estimatedHours: taskSet.estimatedHours || "",
        tasks
      });
    } else if (isOpen && !taskSet) {
      setFormData({
        name: "",
        description: "",
        tags: [],
        estimatedHours: "",
        tasks: []
      });
    }
  }, [isOpen, taskSet]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name?.trim()) {
      alert("Task set name is required");
      return;
    }

    setSaving(true);

    try {
      // Convert tasks array to taskRefs format for backend
      const taskRefs = formData.tasks.map((task, index) => ({
        taskId: task.registryId || task._id,
        weight: task.weight || 1,
        sortOrder: index
      }));

      const payload = {
        name: formData.name,
        description: formData.description,
        tags: Array.isArray(formData.tags) 
          ? formData.tags 
          : formData.tags.split(",").map(t => t.trim()).filter(Boolean),
        estimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : undefined,
        taskRefs
      };

      if (taskSet) {
        await axios.put(`/registry/task-sets/${taskSet._id}`, payload);
      } else {
        await axios.post("/registry/task-sets", payload);
      }

      onSaved && onSaved();
      onClose();
    } catch (error) {
      console.error("Error saving task set:", error);
      alert("Failed to save task set");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">
            {taskSet ? "Edit Task Set" : "New Task Set"}
          </h2>

          {taskSet?.status === "published" && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> This task set is published. You can only edit name, description, and tags. 
                To modify tasks, create a new version.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <TaskSetForm
              value={formData}
              onChange={setFormData}
              mode="registry"
              showSaveToRegistry={false}
              disabled={saving || taskSet?.status === "published"}
            />

            <div className="flex justify-end gap-2 pt-4 mt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="solid" disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TaskSetEditor;
